/**
 * Landing del módulo Reportes — Fase 7.3
 *
 * Muestra los 7 reportes de M13 como cards. Cada card lleva (cuando esté
 * implementada) a su subruta `/reportes/:slug`.
 *
 * Documentos relacionados:
 *   - requerimientos.md § M13 (Reportes)
 *   - DECISIONES.md #034 (stack reportes)
 *
 * Estado: los helpers de PDF (pdf.ts) y Excel (excel.ts) ya están listos;
 * faltan los 7 reportes individuales que se irán activando uno por uno.
 */

import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Icon,
  Grid,
  Tooltip,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';

// ── Configuración de reportes ─────────────────────────────────────────────────

interface ReporteCard {
  slug: string;
  titulo: string;
  descripcion: string;
  icon: string; // Material Icons name
  nuevo?: boolean;
  /** Si false, se muestra como "Próximamente" y la card no es clickeable */
  disponible: boolean;
}

const REPORTES: ReporteCard[] = [
  {
    slug: 'detalle-ddjj',
    titulo: 'Detalle DDJJ por empresa',
    descripcion:
      'Listado de empleados, sueldos y aportes declarados por una empresa en un período.',
    icon: 'description',
    disponible: true,
  },
  {
    slug: 'personal-empresa',
    titulo: 'Personal por empresa',
    descripcion:
      'Padrón actual de afiliados activos con relación laboral vigente en cada empresa.',
    icon: 'people',
    disponible: true,
  },
  {
    slug: 'movimiento-relaciones',
    titulo: 'Movimiento de relaciones laborales',
    descripcion:
      'Altas y bajas de afiliados por empresa en un rango de fechas. Incluye motivos de baja.',
    icon: 'swap_vert',
    disponible: true,
  },
  {
    slug: 'cuenta-corriente',
    titulo: 'Cuenta corriente',
    descripcion:
      'Resumen y detalle de la cuenta corriente de una empresa con saldos por período.',
    icon: 'account_balance_wallet',
    disponible: true,
  },
  {
    slug: 'cupones-no-pagados',
    titulo: 'Cupones no pagados',
    descripcion:
      'Cupones pendientes o vencidos con monto, antigüedad y empresa correspondiente.',
    icon: 'receipt_long',
    disponible: true,
  },
  {
    slug: 'deuda-por-empresa',
    titulo: 'Deuda por empresa',
    descripcion:
      'Ranking de empresas con deuda activa, monto total adeudado y antigüedad promedio.',
    icon: 'trending_down',
    disponible: true,
    nuevo: true,
  },
  {
    slug: 'recaudacion-por-periodo',
    titulo: 'Recaudación por período',
    descripcion:
      'Serie mensual de los últimos 12 meses: cupones emitidos vs acreditados y % de acreditación.',
    icon: 'trending_up',
    disponible: true,
    nuevo: true,
  },
];

// ── Componente ───────────────────────────────────────────────────────────────

export default function ReportesHome() {
  return (
    <Box>
      {/* Encabezado */}
      <Stack direction="row" spacing={2} alignItems="center" mb={1}>
        <AssessmentIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Reportes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Exportá los datos del sistema a PDF o Excel. Cada reporte tiene
            sus propios filtros y se puede previsualizar antes de descargar.
          </Typography>
        </Box>
      </Stack>

      {/* Grid de cards */}
      <Grid container spacing={2} mt={1}>
        {REPORTES.map((r) => (
          <Grid item xs={12} sm={6} md={4} key={r.slug}>
            <ReporteCardComponent reporte={r} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ── Sub-componente: card individual ──────────────────────────────────────────

function ReporteCardComponent({ reporte }: { reporte: ReporteCard }) {
  const inner = (
    <CardContent>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={1}>
        <Box
          sx={{
            bgcolor: 'primary.light',
            color: 'primary.main',
            borderRadius: 1.5,
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon>{reporte.icon}</Icon>
        </Box>
        <Box flex={1}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight={600}>
              {reporte.titulo}
            </Typography>
            {reporte.nuevo && (
              <Chip
                label="Nuevo"
                size="small"
                color="success"
                sx={{ height: 18, fontSize: 10 }}
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {reporte.descripcion}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" mt={1}>
        <Chip
          label={reporte.disponible ? 'Disponible' : 'Próximamente'}
          size="small"
          color={reporte.disponible ? 'primary' : 'default'}
          variant={reporte.disponible ? 'filled' : 'outlined'}
        />
      </Stack>
    </CardContent>
  );

  if (!reporte.disponible) {
    return (
      <Tooltip title="Reporte en desarrollo. Próximamente.">
        <Card
          variant="outlined"
          sx={{ opacity: 0.7, cursor: 'not-allowed', height: '100%' }}
        >
          {inner}
        </Card>
      </Tooltip>
    );
  }

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea
        component={RouterLink}
        to={`/reportes/${reporte.slug}`}
        sx={{ height: '100%' }}
      >
        {inner}
      </CardActionArea>
    </Card>
  );
}
